import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { User, Buildings, Bell, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const Profile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const isSetup = location.state?.setup;
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  
  // Profile form
  const [profile, setProfile] = useState({
    org_name: "",
    ein: "",
    mission: "",
    address: "",
    city: "",
    county: "",
    state: "SC",
    zip_code: "",
    phone: "",
    website: "",
    annual_budget: "",
    staff_count: 0,
    founding_year: new Date().getFullYear(),
    focus_areas: [],
    open_to_collaboration: true
  });
  const [counties, setCounties] = useState([]);
  const [focusInput, setFocusInput] = useState("");

  const FOCUS_SUGGESTIONS = [
    "Education","Youth Development","Health & Wellness","Food Security","Housing",
    "Arts & Culture","Environment","Economic Mobility","Mental Health","Workforce Development",
    "Disability Services","Veterans","Seniors","Faith-Based","Community Development",
    "Animal Welfare","Legal Aid","Homelessness","Domestic Violence","Literacy"
  ];

  // Notification preferences
  const [notifications, setNotifications] = useState({
    email: true,
    push: true
  });

  useEffect(() => {
    if (user?.nonprofit_profile) {
      setProfile(prev => ({
        ...prev,
        ...user.nonprofit_profile,
        focus_areas: user.nonprofit_profile.focus_areas || [],
        open_to_collaboration: user.nonprofit_profile.open_to_collaboration !== false,
        county: user.nonprofit_profile.county || ""
      }));
    }
    if (user?.notification_preferences) {
      setNotifications(user.notification_preferences);
    }
  }, [user]);

  useEffect(() => {
    axios.get(`${API}/collaboration/counties`)
      .then(res => setCounties(res.data.counties || []))
      .catch(() => setCounties([]));
  }, []);

  const addFocusArea = (val) => {
    const v = (val || "").trim();
    if (!v) return;
    if ((profile.focus_areas || []).map(f => f.toLowerCase()).includes(v.toLowerCase())) return;
    setProfile(prev => ({ ...prev, focus_areas: [...(prev.focus_areas || []), v] }));
    setFocusInput("");
  };
  const removeFocusArea = (val) => {
    setProfile(prev => ({ ...prev, focus_areas: (prev.focus_areas || []).filter(f => f !== val) }));
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const saveProfile = async () => {
    if (!profile.org_name) {
      toast.error("Organization name is required");
      return;
    }

    setLoading(true);
    try {
      // Match backend method: POST for setup, PUT for update
      // (Bug fix: was always POSTing, which 405'd on /profile/update for existing profiles)
      const isUpdate = Boolean(user?.nonprofit_profile?.org_name);
      const url = `${API}${isUpdate ? "/profile/update" : "/profile/setup"}`;
      await (isUpdate ? axios.put(url, profile) : axios.post(url, profile));
      await refreshUser();
      toast.success("Profile saved successfully");

      if (isSetup) {
        navigate("/dashboard");
      }
    } catch (error) {
      // Surface the actual backend error so users know what to fix
      const detail = error.response?.data?.detail;
      const friendly = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : detail || error.message || "Please try again or contact support.";
      toast.error(`Couldn't save profile: ${friendly}`);
    } finally {
      setLoading(false);
    }
  };

  const saveNotifications = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/profile/notifications`, notifications);
      toast.success("Notification preferences updated");
    } catch (error) {
      toast.error("Failed to update preferences");
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/email/test`, {});
      toast.success(res.data.message || "Test email sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setLoading(false);
    }
  };

  const budgetOptions = [
    "Under $50,000",
    "$50,000 - $100,000",
    "$100,000 - $250,000",
    "$250,000 - $500,000",
    "$500,000 - $1,000,000",
    "Over $1,000,000"
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="profile-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isSetup ? "Complete Your Profile" : "Profile Settings"}
          </h1>
          <p className="text-zinc-400 mt-1">
            {isSetup 
              ? "Set up your nonprofit profile to start applying for grants" 
              : "Manage your nonprofit profile and preferences"}
          </p>
        </div>

        {/* Tabs */}
        {!isSetup && (
          <div className="flex gap-2 border-b border-zinc-800 pb-2">
            <Button 
              variant="ghost"
              onClick={() => setActiveTab("profile")}
              className={activeTab === "profile" ? "text-emerald-400" : "text-zinc-400"}
              data-testid="tab-profile"
            >
              <Buildings className="w-4 h-4 mr-2" />
              Organization
            </Button>
            <Button 
              variant="ghost"
              onClick={() => setActiveTab("notifications")}
              className={activeTab === "notifications" ? "text-emerald-400" : "text-zinc-400"}
              data-testid="tab-notifications"
            >
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </Button>
          </div>
        )}

        {/* Profile Form */}
        {(activeTab === "profile" || isSetup) && (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Buildings weight="duotone" className="w-5 h-5 text-emerald-400" />
                Nonprofit Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org_name">Organization Name *</Label>
                  <Input 
                    id="org_name"
                    value={profile.org_name}
                    onChange={(e) => handleProfileChange("org_name", e.target.value)}
                    placeholder="e.g., Green Roots SC"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="input-org-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ein">EIN (Tax ID)</Label>
                  <Input 
                    id="ein"
                    value={profile.ein}
                    onChange={(e) => handleProfileChange("ein", e.target.value)}
                    placeholder="XX-XXXXXXX"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="input-ein"
                  />
                </div>
              </div>

              {/* Mission */}
              <div className="space-y-2">
                <Label htmlFor="mission">Mission Statement</Label>
                <Textarea 
                  id="mission"
                  value={profile.mission}
                  onChange={(e) => handleProfileChange("mission", e.target.value)}
                  placeholder="Describe your organization's mission and impact..."
                  className="bg-zinc-900 border-zinc-800 min-h-24"
                  data-testid="input-mission"
                />
              </div>

              {/* Address */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address">Street Address</Label>
                  <Input 
                    id="address"
                    value={profile.address}
                    onChange={(e) => handleProfileChange("address", e.target.value)}
                    placeholder="123 Main Street"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="input-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input 
                    id="city"
                    value={profile.city}
                    onChange={(e) => handleProfileChange("city", e.target.value)}
                    placeholder="Charleston"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="input-city"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select value={profile.state} onValueChange={(v) => handleProfileChange("state", v)}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="input-state">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="SC">South Carolina</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="county">County</Label>
                  <Select value={profile.county || ""} onValueChange={(v) => handleProfileChange("county", v)}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="input-county">
                      <SelectValue placeholder="Select county" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 max-h-80">
                      {counties.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip_code">ZIP Code</Label>
                  <Input 
                    id="zip_code"
                    value={profile.zip_code}
                    onChange={(e) => handleProfileChange("zip_code", e.target.value)}
                    placeholder="29401"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="input-zip"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input 
                    id="phone"
                    value={profile.phone}
                    onChange={(e) => handleProfileChange("phone", e.target.value)}
                    placeholder="(843) 555-0123"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input 
                    id="website"
                    value={profile.website}
                    onChange={(e) => handleProfileChange("website", e.target.value)}
                    placeholder="https://yourorg.org"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="input-website"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="annual_budget">Annual Budget</Label>
                  <Select value={profile.annual_budget} onValueChange={(v) => handleProfileChange("annual_budget", v)}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="input-budget">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {budgetOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="staff_count">Number of Staff</Label>
                  <Input 
                    id="staff_count"
                    type="number"
                    value={profile.staff_count || ""}
                    onChange={(e) => handleProfileChange("staff_count", parseInt(e.target.value) || 0)}
                    placeholder="10"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="input-staff"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="founding_year">Year Founded</Label>
                  <Input 
                    id="founding_year"
                    type="number"
                    value={profile.founding_year || ""}
                    onChange={(e) => handleProfileChange("founding_year", parseInt(e.target.value) || 0)}
                    placeholder="2010"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="input-year"
                  />
                </div>
              </div>

              {/* Focus Areas */}
              <div className="space-y-2">
                <Label htmlFor="focus_areas">Focus Areas</Label>
                <p className="text-xs text-zinc-500">Add tags that describe your programs. Used for grant matching & cross-collaboration.</p>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="focus-area-list">
                  {(profile.focus_areas || []).map((fa) => (
                    <span
                      key={fa}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs rounded-sm"
                      data-testid={`focus-chip-${fa.toLowerCase().replace(/\s+/g,'-')}`}
                    >
                      {fa}
                      <button
                        type="button"
                        onClick={() => removeFocusArea(fa)}
                        className="text-emerald-400 hover:text-white ml-1"
                        data-testid={`focus-remove-${fa.toLowerCase().replace(/\s+/g,'-')}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={focusInput}
                    onChange={(e) => setFocusInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFocusArea(focusInput); } }}
                    placeholder="Type a focus area and press Enter (e.g., Youth Development)"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="input-focus-area"
                  />
                  <Button
                    type="button"
                    onClick={() => addFocusArea(focusInput)}
                    variant="outline"
                    className="border-zinc-700 rounded-sm"
                    data-testid="add-focus-btn"
                  >
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {FOCUS_SUGGESTIONS.filter(s => !(profile.focus_areas || []).map(f=>f.toLowerCase()).includes(s.toLowerCase())).slice(0,10).map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => addFocusArea(s)}
                      className="text-xs px-2 py-0.5 border border-zinc-800 text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/40 rounded-sm"
                      data-testid={`focus-suggest-${s.toLowerCase().replace(/\s+/g,'-')}`}
                    >
                      + {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cross-Collaboration opt-in */}
              <div className="flex items-center justify-between py-3 border-t border-zinc-800">
                <div>
                  <p className="font-medium text-sm">Open to Collaboration</p>
                  <p className="text-xs text-zinc-500">Let other SC nonprofits find you for joint grant applications.</p>
                </div>
                <Switch
                  checked={profile.open_to_collaboration !== false}
                  onCheckedChange={(v) => handleProfileChange("open_to_collaboration", v)}
                  data-testid="switch-collab"
                />
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4">
                <Button 
                  onClick={saveProfile}
                  disabled={loading}
                  className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                  data-testid="save-profile-btn"
                >
                  {loading ? "Saving..." : (isSetup ? "Complete Setup" : "Save Profile")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notification Settings */}
        {activeTab === "notifications" && !isSetup && (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell weight="duotone" className="w-5 h-5 text-emerald-400" />
                Notification Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between py-4 border-b border-zinc-800">
                <div>
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-zinc-400">Receive updates about new grants and application status via email</p>
                </div>
                <Switch 
                  checked={notifications.email}
                  onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email: checked }))}
                  data-testid="switch-email"
                />
              </div>
              <div className="flex items-center justify-between py-4">
                <div>
                  <p className="font-medium">Push Notifications</p>
                  <p className="text-sm text-zinc-400">Receive in-app notifications for important updates</p>
                </div>
                <Switch 
                  checked={notifications.push}
                  onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, push: checked }))}
                  data-testid="switch-push"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  onClick={sendTestEmail}
                  disabled={loading}
                  variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800 rounded-sm"
                  data-testid="send-test-email-btn"
                >
                  {loading ? "Sending..." : "Send Test Email"}
                </Button>
                <Button 
                  onClick={saveNotifications}
                  disabled={loading}
                  className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                  data-testid="save-notifications-btn"
                >
                  {loading ? "Saving..." : "Save Preferences"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscription Info */}
        {!isSetup && (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User weight="duotone" className="w-5 h-5 text-emerald-400" />
                Subscription
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium capitalize">{user?.subscription_tier || "free"} Plan</p>
                  <p className="text-sm text-zinc-400">
                    {user?.subscription_tier === "pro" 
                      ? "Unlimited Flash Drafts + Post-Grant Manager"
                      : "1 Flash Draft per month"}
                  </p>
                </div>
                {user?.subscription_tier !== "pro" && (
                  <Button 
                    onClick={() => navigate("/pricing")}
                    className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                    data-testid="upgrade-btn"
                  >
                    Upgrade to Premium
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Profile;
